import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import MediaPickerInput from './MediaPickerInput';
const DEFAULT_INHERITS = ['identity', 'background', 'spacing', 'text', 'animation', 'custom_code'];
const SIDE_PRESETS = ['xs', 'sm', 'md', 'lg', 'xl'];
const BREAKPOINTS = [
    { id: 'sm', name: 'SM (≥576)' },
    { id: 'md', name: 'MD (≥768)' },
    { id: 'lg', name: 'LG (≥992)' },
];
const WIDTH_PRESETS = [
    { label: '1/4', n: 3 },
    { label: '1/3', n: 4 },
    { label: '1/2', n: 6 },
    { label: '2/3', n: 8 },
    { label: '3/4', n: 9 },
    { label: 'Full', n: 12 },
];
export default function Inspector({ block, onChange, onSave, onOpenCustomCode }) {
    // Local draft. When the parent passes onChange (Phase 3), every patch fires
    // it immediately and the parent's state is the source of truth — but we
    // still keep `draft` in sync so unmount/remount on block-id change is clean.
    const [draft, setDraft] = useState(block.settings ?? {});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [brand, setBrand] = useState(null);
    const [registry, setRegistry] = useState(null);
    useEffect(() => { setDraft(block.settings ?? {}); setError(null); }, [block.id, block.settings]);
    useEffect(() => {
        let alive = true;
        api.brand().then(({ brand }) => { if (alive)
            setBrand(brand); }).catch(() => { });
        api.getBlockSchemas().then((reg) => { if (alive)
            setRegistry(reg); }).catch(() => { });
        return () => { alive = false; };
    }, []);
    const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(block.settings ?? {}), [draft, block.settings]);
    function patch(key, value) {
        setDraft((d) => {
            const next = { ...d };
            if (value === '' || value === undefined || value === null)
                delete next[key];
            else
                next[key] = value;
            onChange?.(next);
            return next;
        });
    }
    async function save() {
        if (!onSave)
            return;
        setBusy(true);
        setError(null);
        try {
            await onSave(draft);
        }
        catch (e) {
            setError(e?.message ?? 'Save failed');
        }
        finally {
            setBusy(false);
        }
    }
    function reset() {
        const original = block.settings ?? {};
        setDraft(original);
        onChange?.(original);
    }
    const colorTokens = brand ? Object.keys(brand.colors) : [];
    // Resolve the schema descriptor for this block type. Unknown types fall
    // back to the default base groups so the user always gets identity/custom
    // code controls even on a type the registry doesn't yet declare.
    const descriptor = (registry?.schemas?.[block.type]) ?? { inherits: DEFAULT_INHERITS };
    const baseGroups = registry?.base ?? {};
    const inheritIds = descriptor.inherits ?? DEFAULT_INHERITS;
    const typeGroups = descriptor.fields ?? [];
    const suppress = new Set(descriptor.suppress ?? []);
    // Show-if context: any block-level key the schema may key off (layout, type).
    const showCtx = { layout: block.layout ?? '', type: block.type, ...draft };
    return (_jsxs("div", { className: "inspector", children: [_jsx("div", { className: "inspector-head", children: _jsxs("div", { children: [_jsxs("div", { className: "inspector-title", children: [block.type, block.layout ? `:${block.layout}` : ''] }), _jsxs("div", { className: "muted small", children: ["#", block.id] })] }) }), _jsxs("div", { className: "inspector-body", children: [!registry && _jsx("div", { className: "muted small", children: "Loading fields\u2026" }), typeGroups.map((g) => (_jsx(Group, { title: g.group, children: g.fields.filter((f) => !suppress.has(f.key) && matchesShowIf(f.show_if, showCtx)).map((f) => _jsx(FieldRenderer, { field: f, draft: draft, patch: patch, colorTokens: colorTokens, onOpenCustomCode: onOpenCustomCode }, f.key)) }, g.group))), inheritIds.map((id) => {
                        const group = baseGroups[id];
                        if (!group)
                            return null;
                        const fields = group.fields.filter((f) => !suppress.has(f.key) && matchesShowIf(f.show_if, showCtx));
                        if (fields.length === 0)
                            return null;
                        return (_jsx(Group, { title: group.label, defaultOpen: id !== 'animation' && id !== 'custom_code', children: fields.map((f) => _jsx(FieldRenderer, { field: f, draft: draft, patch: patch, colorTokens: colorTokens, onOpenCustomCode: onOpenCustomCode }, f.key)) }, id));
                    })] }), onSave && (_jsxs("div", { className: "inspector-foot", children: [error && _jsx("div", { className: "err", children: error }), _jsxs("div", { className: "actions", children: [_jsx("button", { className: "ghost", disabled: !dirty || busy, onClick: reset, children: "Reset" }), _jsx("button", { disabled: !dirty || busy, onClick: save, children: busy ? 'Saving…' : 'Save' })] })] }))] }));
}
function FieldRenderer({ field, draft, patch, colorTokens, onOpenCustomCode }) {
    const value = draft[field.key];
    switch (field.type) {
        case 'string':
            return (_jsx(Field, { label: field.label, children: _jsx("input", { type: "text", placeholder: field.placeholder, value: getStr(value), onChange: (e) => patch(field.key, e.target.value) }) }));
        case 'length':
            return (_jsx(Field, { label: field.label, children: _jsx("input", { type: "text", placeholder: field.placeholder ?? 'auto / 50vh / 480px', value: getStr(value), onChange: (e) => patch(field.key, e.target.value) }) }));
        case 'enum': {
            const opts = (field.options ?? []).map((o) => ({ id: o, label: o || '— none —' }));
            return (_jsx(Field, { label: field.label, children: _jsxs("select", { value: getStr(value), onChange: (e) => patch(field.key, e.target.value), children: [opts.some((o) => o.id === '') ? null : _jsx("option", { value: "", children: "\u2014 none \u2014" }), opts.map((o) => _jsx("option", { value: o.id, children: o.label }, o.id || 'none'))] }) }));
        }
        case 'length-preset': {
            const presets = field.presets ?? [];
            return (_jsx(PresetField, { label: field.label, options: presets, value: getStr(value), onChange: (v) => patch(field.key, v), showNone: true }));
        }
        case 'color':
            return (_jsx(ColorField, { label: field.label, tokens: colorTokens, value: getStr(value), onChange: (v) => patch(field.key, v) }));
        case 'media-image':
            return (_jsx(Field, { label: field.label, children: _jsx(MediaPickerInput, { accept: "image", value: getStr(value), onChange: (v) => patch(field.key, v), placeholder: field.placeholder ?? 'media/...' }) }));
        case 'media-video':
            return (_jsx(Field, { label: field.label, children: _jsx(MediaPickerInput, { accept: "video", value: getStr(value), onChange: (v) => patch(field.key, v), placeholder: field.placeholder ?? 'media/...' }) }));
        case 'bool':
            return (_jsx(Field, { label: field.label, children: _jsx("input", { type: "checkbox", checked: !!value, onChange: (e) => patch(field.key, e.target.checked || null) }) }));
        case 'int':
            return (_jsx(Field, { label: field.label, children: _jsx("input", { type: "number", value: typeof value === 'number' ? value : '', onChange: (e) => {
                        const n = parseInt(e.target.value, 10);
                        patch(field.key, Number.isNaN(n) ? null : n);
                    } }) }));
        case 'col-width':
            return (_jsx(Field, { label: field.label, children: _jsx(WidthField, { value: getObj(value), onChange: (v) => patch(field.key, v) }) }));
        case 'sides-preset':
            return (_jsx(Field, { label: field.label, children: _jsx(PaddingPerSideField, { value: getObj(value), onChange: (v) => patch(field.key, Object.keys(v).length ? v : null) }) }));
        case 'raw': {
            const summary = summarizeCode(field.lang === 'css' ? 'CSS' : (field.lang === 'javascript' ? 'JS' : (field.lang ?? 'code').toUpperCase()), getStr(value));
            return (_jsx(Field, { label: field.label, children: _jsxs("div", { className: "code-summary", children: [_jsx("button", { type: "button", className: "ghost", onClick: onOpenCustomCode, children: '{ } Edit' }), _jsx("span", { className: "muted small", children: summary })] }) }));
        }
    }
    return null;
}
/** Evaluate a simple AND-of-equals show_if descriptor against a context. */
function matchesShowIf(showIf, ctx) {
    if (!showIf)
        return true;
    for (const [k, v] of Object.entries(showIf)) {
        if (ctx[k] !== v)
            return false;
    }
    return true;
}
// ============================================================
// Field helpers (shared with the legacy hand-built field sets)
// ============================================================
function Group({ title, children, defaultOpen = true }) {
    const [open, setOpen] = useState(defaultOpen);
    return (_jsxs("section", { className: `ins-group${open ? ' open' : ''}`, children: [_jsxs("button", { type: "button", className: "ins-group-head", onClick: () => setOpen((o) => !o), children: [_jsx("span", { className: "caret", children: open ? '▾' : '▸' }), _jsx("span", { children: title })] }), open && _jsx("div", { className: "ins-group-body", children: children })] }));
}
function Field({ label, children }) {
    return (_jsxs("label", { className: "field", children: [_jsx("span", { children: label }), children] }));
}
function PresetField({ label, options, value, onChange, showNone = true, }) {
    return (_jsx(Field, { label: label, children: _jsxs("div", { className: "preset-row", children: [showNone && (_jsx("button", { type: "button", className: value === '' ? 'preset active' : 'preset', onClick: () => onChange(''), children: "\u2013" })), options.map((o) => (_jsx("button", { type: "button", className: value === o ? 'preset active' : 'preset', onClick: () => onChange(o), children: o || '–' }, o || 'empty')))] }) }));
}
function ColorField({ label, tokens, value, onChange, }) {
    return (_jsx(Field, { label: label, children: _jsxs("div", { className: "color-row", children: [_jsxs("select", { value: tokens.includes(value) ? value : '', onChange: (e) => onChange(e.target.value), children: [_jsx("option", { value: "", children: "\u2014 none \u2014" }), tokens.map((t) => _jsx("option", { value: t, children: t }, t))] }), _jsx("input", { type: "text", placeholder: "or raw value (e.g. #fff)", value: tokens.includes(value) ? '' : value, onChange: (e) => onChange(e.target.value) })] }) }));
}
function WidthField({ value, onChange, }) {
    function setBp(bp, n) {
        const next = {};
        for (const b of BREAKPOINTS) {
            const cur = (value && typeof value[b.id] === 'number') ? value[b.id] : null;
            if (b.id === bp) {
                if (n !== null)
                    next[b.id] = n;
            }
            else if (cur !== null) {
                next[b.id] = cur;
            }
        }
        onChange(next);
    }
    return (_jsx("div", { className: "width-field", children: BREAKPOINTS.map((bp) => {
            const current = (value && typeof value[bp.id] === 'number') ? value[bp.id] : null;
            return (_jsxs("div", { className: "width-bp", children: [_jsx("span", { className: "width-bp-label", title: bp.name, children: bp.id.toUpperCase() }), _jsxs("div", { className: "preset-row", children: [_jsx("button", { type: "button", className: current === null ? 'preset active' : 'preset', title: "Inherit from smaller breakpoint", onClick: () => setBp(bp.id, null), children: "\u2013" }), WIDTH_PRESETS.map((p) => (_jsx("button", { type: "button", className: current === p.n ? 'preset active' : 'preset', onClick: () => setBp(bp.id, p.n), title: `col-${bp.id}-${p.n}`, children: p.label }, p.n))), _jsx("input", { type: "number", min: 1, max: 12, placeholder: "1-12", value: current !== null && !WIDTH_PRESETS.find((p) => p.n === current) ? current : '', onChange: (e) => {
                                    const n = parseInt(e.target.value, 10);
                                    if (Number.isNaN(n))
                                        setBp(bp.id, null);
                                    else if (n >= 1 && n <= 12)
                                        setBp(bp.id, n);
                                } })] })] }, bp.id));
        }) }));
}
function PaddingPerSideField({ value, onChange, }) {
    const sides = [
        { k: 't', label: 'Top' },
        { k: 'r', label: 'Right' },
        { k: 'b', label: 'Bottom' },
        { k: 'l', label: 'Left' },
    ];
    function setSide(k, v) {
        const next = { ...value };
        if (v)
            next[k] = v;
        else
            delete next[k];
        for (const key of Object.keys(next))
            if (!next[key])
                delete next[key];
        onChange(next);
    }
    return (_jsx("div", { className: "padding-field", children: sides.map(({ k, label }) => {
            const cur = (value && typeof value[k] === 'string') ? value[k] : '';
            return (_jsxs("div", { className: "padding-side", children: [_jsx("span", { className: "padding-side-label", children: label }), _jsxs("div", { className: "preset-row", children: [_jsx("button", { type: "button", className: cur === '' ? 'preset active' : 'preset', onClick: () => setSide(k, ''), children: "\u2013" }), SIDE_PRESETS.map((p) => (_jsx("button", { type: "button", className: cur === p ? 'preset active' : 'preset', onClick: () => setSide(k, p), children: p }, p)))] })] }, k));
        }) }));
}
function getStr(v, fallback = '') {
    return typeof v === 'string' ? v : v == null ? fallback : String(v);
}
function getObj(v) {
    return v && typeof v === 'object' && !Array.isArray(v) ? v : {};
}
function summarizeCode(label, value) {
    if (!value || !value.trim())
        return `${label}: empty`;
    const lines = value.split('\n').length;
    return `${label}: ${value.length}b · ${lines}L`;
}
